import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiService } from '../utils/api';

export type RoleType = 'planner' | 'executor' | 'beneficiary';

export interface RoleData {
  role: RoleType;
  estateOwnerId?: string;
  estateOwnerName?: string;
  estateOwnerEmail?: string;
  inviteStatus?: string;
  hasApprovedDeathCert?: boolean;
}

interface ExecutorRoleResponse {
  estateOwnerId?: string;
  ownerName?: string;
  ownerEmail?: string;
  inviteStatus?: string;
  hasApprovedDeathCert?: boolean;
}

interface BeneficiaryRoleResponse {
  estateOwnerId?: string;
  ownerName?: string;
  ownerEmail?: string;
  inviteStatus?: string;
}

interface UserRolesResponse {
  success?: boolean;
  message?: string;
  executorFor?: ExecutorRoleResponse[];
  beneficiaryFor?: BeneficiaryRoleResponse[];
}

interface RoleContextValue {
  activeRole: RoleData | null;
  availableRoles: RoleData[];
  switchRole: (role: RoleData) => void;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

export const useRole = (): RoleContextValue => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within RoleProvider');
  }
  return context;
};

interface RoleProviderProps {
  children: ReactNode;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const [activeRole, setActiveRole] = useState<RoleData | null>(null);
  const [availableRoles, setAvailableRoles] = useState<RoleData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTokenRef = useRef<string | null>(null);

  const fetchRoles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('account-token');
      lastTokenRef.current = token;
      if (!token) {
        setAvailableRoles([]);
        setActiveRole(null);
        setIsLoading(false);
        return;
      }

      const response = (await apiService.request('/api/user/roles', {
        method: 'GET',
      })) as UserRolesResponse;

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch roles');
      }

      const roles: RoleData[] = [];

      // Always add Planner role (everyone has their own estate)
      roles.push({
        role: 'planner'
      });

      // Add Executor roles
      if (response.executorFor && response.executorFor.length > 0) {
        response.executorFor.forEach((exec) => {
          if (!exec.estateOwnerId) return;

          roles.push({
            role: 'executor',
            estateOwnerId: exec.estateOwnerId,
            estateOwnerName: exec.ownerName,
            estateOwnerEmail: exec.ownerEmail,
            inviteStatus: exec.inviteStatus,
            hasApprovedDeathCert: exec.hasApprovedDeathCert,
          });
        });
      }

      // Add Beneficiary roles
      if (response.beneficiaryFor && response.beneficiaryFor.length > 0) {
        response.beneficiaryFor.forEach((ben) => {
          if (!ben.estateOwnerId) return;

          roles.push({
            role: 'beneficiary',
            estateOwnerId: ben.estateOwnerId,
            estateOwnerName: ben.ownerName,
            estateOwnerEmail: ben.ownerEmail,
            inviteStatus: ben.inviteStatus,
          });
        });
      }

      setAvailableRoles(roles);

      // Set active role from localStorage or default to planner
      const savedRoleString = localStorage.getItem('active-role');
      if (savedRoleString) {
        try {
          const savedRole = JSON.parse(savedRoleString);
          const matchingRole = roles.find(
            r => r.role === savedRole.role && r.estateOwnerId === savedRole.estateOwnerId
          );
          if (matchingRole) {
            setActiveRole(matchingRole);
          } else {
            setActiveRole(roles[0]);
          }
        } catch {
          setActiveRole(roles[0]);
        }
      } else {
        setActiveRole(roles[0]);
      }
    } catch (err) {
      console.error('[RoleContext] Failed to fetch roles:', err);
      
      // Check if error is due to invalid session (401)
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (errorMessage.includes('Invalid or expired session') || errorMessage.includes('401')) {
        console.warn('[RoleContext] Session token invalid - clearing session and redirecting to login');
        localStorage.removeItem('account-token');
        localStorage.removeItem('account-refresh-token');
        localStorage.removeItem('estate-plan-session');
        window.location.href = '/login';
        return;
      }
      
      setError(errorMessage);
      setAvailableRoles([{ role: 'planner' }]);
      setActiveRole({ role: 'planner' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const syncRolesAfterTokenChange = () => {
      const token = localStorage.getItem('account-token');

      if (token === lastTokenRef.current) {
        return;
      }

      fetchRoles();
    };

    window.addEventListener('focus', syncRolesAfterTokenChange);
    window.addEventListener('storage', syncRolesAfterTokenChange);

    const intervalId = window.setInterval(syncRolesAfterTokenChange, 500);

    return () => {
      window.removeEventListener('focus', syncRolesAfterTokenChange);
      window.removeEventListener('storage', syncRolesAfterTokenChange);
      window.clearInterval(intervalId);
    };
  }, [fetchRoles]);

  const switchRole = (role: RoleData) => {
    setActiveRole(role);
    localStorage.setItem('active-role', JSON.stringify(role));
    console.log('[RoleContext] Switched to role:', role);
  };

  const refetch = async () => {
    await fetchRoles();
  };

  return (
    <RoleContext.Provider value={{ activeRole, availableRoles, switchRole, isLoading, error, refetch }}>
      {children}
    </RoleContext.Provider>
  );
};
